import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import Toast from '../../components/Toast';
import AIInterviewSetup from './AIInterviewSetup';
import AIInterviewChat from './AIInterviewChat';
import AIInterviewCompletion from './AIInterviewCompletion';
import AIInterviewHistory from './AIInterviewHistory';
import {
  startInterview,
  sendTurn,
  getResult,
  getTranscript,
  getHistory,
  getStatus,
  endInterview,
  sendPresence,
  submitTaskAnswer,
  interviewWsUrl,
} from './utils/aiInterviewApi';
import { parseTranscript } from './utils/transcript';

// ── resume pointer (survives a page refresh; no backend/DB needed) ──
// The backend is already resume-safe (idempotent /start + active-run pointer). This just
// lets the client re-attach to an in-progress run instead of resetting to the setup screen.
const RESUME_KEY = 'aiInterview.resume';
const saveResume = (data) => {
  try { window.localStorage.setItem(RESUME_KEY, JSON.stringify(data)); } catch { /* ignore */ }
};
const loadResume = () => {
  try { return JSON.parse(window.localStorage.getItem(RESUME_KEY) || 'null'); } catch { return null; }
};
const clearResume = () => {
  try { window.localStorage.removeItem(RESUME_KEY); } catch { /* ignore */ }
};

/**
 * AI Interview — public, candidate-facing page.
 * Flow: setup (Session ID + Candidate ID) -> live chat -> completion (saved transcript).
 * Optionally accepts /ai-interview/:sessionId/:candidateId for direct links.
 */
const AIInterviewPage = () => {
  const params = useParams();
  const [phase, setPhase] = useState('setup'); // 'setup' | 'chat' | 'done' | 'history'
  const [booting, setBooting] = useState(true); // resolving a possible resume before first paint
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [sessionId, setSessionId] = useState(params.sessionId || '');
  const [candidateId, setCandidateId] = useState(params.candidateId || '');
  const [messages, setMessages] = useState([]); // { role, text }
  const [input, setInput] = useState('');
  const [currentState, setCurrentState] = useState('');
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ message: '', type: 'error' });
  const [transcript, setTranscript] = useState('');
  const [meta, setMeta] = useState({});
  const [evaluation, setEvaluation] = useState(null);
  const [evaluating, setEvaluating] = useState(false);
  const [resultId, setResultId] = useState(null); // the interview currently shown on the result page
  const [silenceLeft, setSilenceLeft] = useState(null); // seconds until auto-end (UI countdown)
  const [elapsed, setElapsed] = useState(null);         // overall interview elapsed seconds
  const [stage, setStage] = useState('');               // human-readable interview stage
  const [progress, setProgress] = useState({ asked: 0, target: null, total: null });
  const [closingSeconds, setClosingSeconds] = useState(null); // hold-then-summary countdown
  const [activeTask, setActiveTask] = useState(null);
  const [taskAnswer, setTaskAnswer] = useState('');

  const wsRef = useRef(null);
  const limitsRef = useRef({ nudgeAfter: 120, endAfter: 420 });
  const closeDelayRef = useRef(10);
  const endedRef = useRef(false);
  const checkingRef = useRef(false);
  const lastActiveSentRef = useRef(0);

  // Launched with prefilled ids in the URL (the email link / prefill route): the interview should
  // start automatically behind a loader — the candidate must never see the raw Session/Candidate form.
  const autoLaunch = Boolean(params.sessionId && params.candidateId);
  const autoStartedRef = useRef(false);

  const showError = (message) => setToast({ message, type: 'error' });
  const showSuccess = (message) => setToast({ message, type: 'success' });

  const addInterviewer = (text) => {
    if (text && text.trim()) setMessages((prev) => [...prev, { role: 'interviewer', text }]);
  };

  // ── inactivity timer ──────────────────────────────────────────────
  const applyDeadlines = (data) => {
    if (!data) return;
    if (data.current_stage) setStage(data.current_stage);
    setActiveTask(data.active_practical_task || null);
    if (data.active_practical_task) setTaskAnswer('');
    if (data.closing_display_seconds != null) closeDelayRef.current = data.closing_display_seconds;
    // Overall interview timer + progress (updates whether or not finished).
    if (data.interview_elapsed_seconds != null) setElapsed(data.interview_elapsed_seconds);
    setProgress({
      asked: data.questions_asked ?? 0,
      target: data.target_questions ?? null,
      total: data.duration_minutes != null ? data.duration_minutes * 60 : null,
    });
    if (data.finished) {
      setSilenceLeft(null);
      setActiveTask(null);
      return;
    }
    if (data.end_after_seconds != null) {
      limitsRef.current = {
        nudgeAfter: data.nudge_after_seconds ?? limitsRef.current.nudgeAfter,
        endAfter: data.end_after_seconds,
      };
    }
    setSilenceLeft(data.end_in_seconds != null ? data.end_in_seconds : limitsRef.current.endAfter);
  };

  const resetTimer = () => setSilenceLeft(limitsRef.current.endAfter);

  // Tick the silence + overall timers once per second while chatting.
  useEffect(() => {
    if (phase !== 'chat') return undefined;
    const id = setInterval(() => {
      setSilenceLeft((prev) => (prev == null ? null : Math.max(0, prev - 1)));
      if (!endedRef.current) setElapsed((prev) => (prev == null ? prev : prev + 1));
      setActiveTask((prev) => (
        prev && prev.status === 'active'
          ? { ...prev, remaining_seconds: Math.max(0, Number(prev.remaining_seconds || 0) - 1) }
          : prev
      ));
    }, 1000);
    return () => clearInterval(id);
  }, [phase]);

  // Keep the candidate marked "present" while they're engaged with the interview — reading a
  // question, thinking, or composing a long (e.g. coding) answer. Typing fires `sendActive` on each
  // keystroke, but THINK-TIME produces no keystrokes, so without this a candidate who pauses to think
  // (common on coding questions) is counted as silent and gets the "are you still there?" nudge
  // mid-answer. While the interview tab is visible we treat them as engaged and refresh both the
  // server-side silence monitor and the local countdown; switching/hiding the tab lets silence resume.
  useEffect(() => {
    if (phase !== 'chat') return undefined;
    const id = setInterval(() => {
      if (endedRef.current || loading || activeTask) return;
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      sendActive();   // reset the authoritative server-side silence monitor (WS "active")
      resetTimer();   // keep the local countdown from alarming an engaged candidate
    }, 15000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, loading, activeTask]);

  // After the final message, hold the chat a few seconds so the candidate can read it.
  useEffect(() => {
    if (closingSeconds == null) return undefined;
    if (closingSeconds <= 0) {
      setPhase('done');
      closeWs();
      setClosingSeconds(null);
      return undefined;
    }
    const id = setTimeout(() => setClosingSeconds((s) => (s == null ? null : s - 1)), 1000);
    return () => clearTimeout(id);
  }, [closingSeconds]);

  // When the local clock hits zero, confirm with the server (covers a missed WS push).
  useEffect(() => {
    if (phase !== 'chat' || silenceLeft !== 0 || endedRef.current || checkingRef.current) return;
    checkingRef.current = true;
    (async () => {
      try {
        const st = await getStatus(sessionId.trim(), candidateId.trim());
        if (st.finished) {
          await finishUp();
        } else {
          resetTimer(); // server still considers us active — keep going
        }
      } catch {
        resetTimer();
      } finally {
        checkingRef.current = false;
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [silenceLeft, phase]);

  // ── presence WebSocket (also delivers server-initiated messages) ──
  const connectWs = (sid, cid) => {
    try {
      const ws = new WebSocket(interviewWsUrl(sid));
      wsRef.current = ws;
      ws.onopen = () => {
        try {
          ws.send(JSON.stringify({ type: 'identify', candidate_id: Number(cid) }));
        } catch {
          /* ignore */
        }
      };
      ws.onmessage = (evt) => {
        let msg = {};
        try {
          msg = JSON.parse(evt.data);
        } catch {
          return;
        }
        if (msg.type === 'interviewer_message') {
          // A server-initiated "are you still there?" check-in.
          addInterviewer(msg.interviewer_message);
          applyDeadlines(msg);
        } else if (msg.type === 'interview_ended') {
          addInterviewer(msg.interviewer_message);
          finishUp();
        }
      };
      ws.pingTimer = setInterval(() => {
        try {
          if (ws.readyState === 1) ws.send(JSON.stringify({ type: 'ping' }));
        } catch {
          /* ignore */
        }
      }, 25000);
      ws.onclose = () => clearInterval(ws.pingTimer);
      ws.onerror = () => {};
    } catch {
      /* presence is best-effort */
    }
  };

  const sendActive = () => {
    const now = Date.now();
    if (now - lastActiveSentRef.current < 4000) return; // throttle
    const ws = wsRef.current;
    if (ws && ws.readyState === 1) {
      lastActiveSentRef.current = now;
      try {
        ws.send(JSON.stringify({ type: 'active' }));
      } catch {
        /* ignore */
      }
      return;
    }
    // WS not connected (dropped / never opened) — fall back to the REST presence ping so an
    // actively-typing candidate still resets the server-side silence clock. Without this, when the
    // presence socket is down the backend can't see the keystrokes and fires "are you still there?"
    // mid-answer. `present:true` routes to set_away_by_session(away=False), which refreshes last_activity.
    lastActiveSentRef.current = now;
    try {
      const sid = sessionId.trim();
      const cid = candidateId.trim();
      if (sid && cid) sendPresence(sid, cid, true).catch(() => {});
    } catch {
      /* best-effort */
    }
  };

  const closeWs = () => {
    try {
      if (wsRef.current) {
        clearInterval(wsRef.current.pingTimer);
        wsRef.current.close();
      }
    } catch {
      /* ignore */
    }
  };

  useEffect(() => closeWs, []); // cleanup on unmount

  // Rebuild the chat from the saved transcript (resume / re-entry), falling back to the
  // last interviewer line if the transcript can't be fetched yet.
  const enterChatFromServer = async (sid, cid, fallbackMessage = '') => {
    let msgs = [];
    try {
      const t = await getTranscript(sid, cid);
      msgs = parseTranscript(t.transcript_text || '');
    } catch {
      /* transcript not reachable — fall back below */
    }
    if (!msgs.length && fallbackMessage) msgs = [{ role: 'interviewer', text: fallbackMessage }];
    setMessages(msgs);
  };

  // ── resume an in-progress run after a page refresh / reopen ────────
  useEffect(() => {
    const r = loadResume();
    // When launched with explicit ids in the URL, those are authoritative — never let a stale resume
    // from a previous run (e.g. a mock interview) override the candidate id with a different one.
    if (params.sessionId && params.candidateId
        && (!r || String(r.candidateId) !== String(params.candidateId))) {
      setBooting(false);
      return;
    }
    if (!r || !r.sessionId || !r.candidateId) { setBooting(false); return; }
    (async () => {
      try {
        const st = await getStatus(r.sessionId, r.candidateId);
        if (!st || !st.exists) { clearResume(); setBooting(false); return; }
        setSessionId(r.sessionId);
        setCandidateId(r.candidateId);
        setCurrentState(st.current_state || '');
        if (st.finished) {
          // Interview already ended — go straight to the result page.
          endedRef.current = true;
          setResultId(st.interview_id || null);
          try {
            const d = await getResult(r.sessionId, r.candidateId, st.interview_id || undefined, false);
            setResultId(d.interview_id || st.interview_id || null);
            setTranscript(d.transcript_text || '');
            setMeta(d.metadata || {});
            setEvaluation(d.evaluation || null);
          } catch {
            try {
              const t = await getTranscript(r.sessionId, r.candidateId);
              setTranscript(t.transcript_text || '');
              setMeta(t.metadata || {});
            } catch { /* ignore */ }
          }
          setPhase('done');
        } else {
          // Still in progress — restore the conversation and reconnect.
          endedRef.current = false;
          setProgress((p) => ({ ...p, asked: st.questions_asked ?? 0 }));
          await enterChatFromServer(r.sessionId, r.candidateId);
          setSilenceLeft(limitsRef.current.endAfter);
          setPhase('chat');
          connectWs(r.sessionId, r.candidateId);
        }
      } catch {
        // Network hiccup — keep the pointer and let the candidate retry from setup.
      } finally {
        setBooting(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── handlers ──────────────────────────────────────────────────────
  const loadResult = async (interviewId, refresh = false, basis) => {
    setEvaluating(true);
    try {
      const d = await getResult(sessionId.trim(), candidateId.trim(), interviewId, refresh, basis); // scorecard server-side
      setResultId(d.interview_id || interviewId || null);
      setTranscript(d.transcript_text || '');
      setMeta(d.metadata || {});
      setEvaluation(d.evaluation || null);
      return d.evaluation || null;
    } catch (e) {
      // /result may be missing (backend not restarted) — still show the transcript so the page updates.
      try {
        const t = await getTranscript(sessionId.trim(), candidateId.trim());
        setTranscript(t.transcript_text || '');
        setMeta(t.metadata || {});
      } catch (e2) {
        showError(e2.message);
      }
      setEvaluation(null);
      if (refresh) showError(`Couldn't reach the result endpoint (${e.message}). Is the backend restarted?`);
      return null;
    } finally {
      setEvaluating(false);
    }
  };

  const finishUp = async () => {
    if (endedRef.current) return;
    endedRef.current = true;
    setSilenceLeft(null);
    setClosingSeconds(closeDelayRef.current || 10);  // hold the chat so the closing line is read
    loadResult();                                    // fetch transcript + scorecard in the background
  };

  const handleStart = async () => {
    if (!sessionId.trim() || !candidateId.trim()) {
      showError('Please enter both Session ID and Candidate ID.');
      return;
    }
    const sid = sessionId.trim();
    const cid = candidateId.trim();
    setLoading(true);
    try {
      // /start is idempotent: a new pair greets, an in-progress pair resumes. Either way we
      // rebuild the full conversation from the transcript so re-entering credentials never
      // looks like a fresh interview.
      const data = await startInterview(sid, cid);
      endedRef.current = false;
      await enterChatFromServer(sid, cid, data.interviewer_message);
      setCurrentState(data.current_state);
      applyDeadlines(data);
      setPhase('chat');
      saveResume({ sessionId: sid, candidateId: cid });
      connectWs(sid, cid);
    } catch (e) {
      showError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // Auto-start once the prefilled ids are resolved, so the launched flow shows only a loader.
  useEffect(() => {
    if (autoLaunch && !booting && phase === 'setup' && !autoStartedRef.current
        && sessionId.trim() && candidateId.trim()) {
      autoStartedRef.current = true;
      handleStart();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLaunch, booting, phase, sessionId, candidateId]);

  const handleInputChange = (value) => {
    setInput(value);
    resetTimer(); // typing means the candidate is here
    sendActive();
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    setMessages((prev) => [...prev, { role: 'candidate', text }]);
    resetTimer();
    setLoading(true);
    try {
      const data = await sendTurn(sessionId.trim(), candidateId.trim(), text);
      addInterviewer(data.interviewer_message);
      setCurrentState(data.current_state);
      applyDeadlines(data);
      if (data.finished) await finishUp();
    } catch (e) {
      showError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTaskSubmit = async () => {
    const text = taskAnswer.trim();
    if (!activeTask || !text || loading) return;
    setTaskAnswer('');
    setMessages((prev) => [...prev, { role: 'candidate', text }]);
    setLoading(true);
    try {
      const data = await submitTaskAnswer(
        sessionId.trim(),
        candidateId.trim(),
        activeTask.task_id,
        text,
        `${activeTask.task_id}-${Date.now()}`,
      );
      setActiveTask(null);
      addInterviewer(data.interviewer_message);
      setCurrentState(data.current_state);
      applyDeadlines(data);
      if (data.finished) await finishUp();
    } catch (e) {
      showError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEnd = async () => {
    if (loading) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm('End the interview now? You won’t be able to continue this session.')) return;
    setLoading(true);
    try {
      const data = await endInterview(sessionId.trim(), candidateId.trim(), 'ended_by_candidate');
      addInterviewer(data.interviewer_message);
      setCurrentState(data.current_state);
      await finishUp();
    } catch (e) {
      showError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRecalculate = async (basis = 'current_configuration') => {
    if (evaluating) return;
    const ev = await loadResult(resultId || undefined, true, basis);
    if (ev && ev.refresh_failed) {
      showError(`Recalculation failed: ${ev.refresh_error || 'the scoring model did not respond'} (showing previous score).`);
    } else if (ev && ev.available) {
      showSuccess(`Recalculated: ${ev.overall_score}/100 · ${ev.decision_label}`);
    } else if (ev && ev.available === false) {
      showError(`Recalculation failed: ${ev.error || 'the scoring model did not respond.'}`);
    }
  };

  const handleViewHistory = async () => {
    if (!sessionId.trim() || !candidateId.trim()) {
      showError('Please enter both Session ID and Candidate ID to see past results.');
      return;
    }
    setHistoryLoading(true);
    setPhase('history');
    try {
      const d = await getHistory(sessionId.trim(), candidateId.trim());
      setHistory(d.interviews || []);
    } catch (e) {
      showError(e.message);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleOpenHistory = async (interviewId) => {
    endedRef.current = true;          // viewing a finished run, not a live one
    setMessages([]);
    setTranscript('');
    setEvaluation(null);
    setPhase('done');
    await loadResult(interviewId);
  };

  const handleRestart = () => {
    closeWs();
    clearResume();
    endedRef.current = false;
    setHistory([]);
    setPhase('setup');
    setMessages([]);
    setInput('');
    setCurrentState('');
    setTranscript('');
    setMeta({});
    setEvaluation(null);
    setEvaluating(false);
    setResultId(null);
    setSilenceLeft(null);
    setElapsed(null);
    setStage('');
    setProgress({ asked: 0, target: null, total: null });
    setClosingSeconds(null);
    setActiveTask(null);
    setTaskAnswer('');
  };

  if (booting) {
    return (
      <div className="min-h-screen w-full bg-[#F6F7F9] dark:bg-gray-950 flex items-center justify-center px-4">
        <p className="text-slate-500 dark:text-slate-400 text-sm animate-pulse">Resuming…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-[#F6F7F9] dark:bg-gray-950 flex flex-col items-center py-10 px-4">
      <div className="w-full max-w-2xl">
        <header className="text-center mb-6">
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white">AI Interview</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">
            A friendly, AI-led conversation about your background and experience.
          </p>
        </header>

        {phase === 'setup' && (
          autoLaunch ? (
            <div className="flex flex-col items-center gap-3 py-20">
              <span className="h-8 w-8 animate-spin rounded-full border-2 border-primary-500 border-t-transparent dark:border-violet-500" />
              <p className="text-sm text-slate-500 dark:text-slate-400">Starting your interview…</p>
            </div>
          ) : (
            <AIInterviewSetup
              sessionId={sessionId}
              candidateId={candidateId}
              setSessionId={setSessionId}
              setCandidateId={setCandidateId}
              onStart={handleStart}
              onViewHistory={handleViewHistory}
              loading={loading}
            />
          )
        )}

        {phase === 'history' && (
          <AIInterviewHistory
            interviews={history}
            loading={historyLoading}
            onOpen={handleOpenHistory}
            onBack={() => setPhase('setup')}
          />
        )}

        {phase === 'chat' && (
          <AIInterviewChat
            messages={messages}
            input={input}
            onInputChange={handleInputChange}
            onSend={handleSend}
            onEnd={handleEnd}
            loading={loading}
            currentState={currentState}
            stage={stage}
            silenceLeft={silenceLeft}
            nudgeAfter={limitsRef.current.nudgeAfter}
            endAfter={limitsRef.current.endAfter}
            elapsed={elapsed}
            total={progress.total}
            questionsAsked={progress.asked}
            targetQuestions={progress.target}
            closingSeconds={closingSeconds}
            activeTask={activeTask}
            taskAnswer={taskAnswer}
            onTaskAnswerChange={setTaskAnswer}
            onTaskSubmit={handleTaskSubmit}
          />
        )}

        {phase === 'done' && (
          <AIInterviewCompletion
            transcript={transcript}
            meta={meta}
            evaluation={evaluation}
            evaluating={evaluating}
            onRecalculate={handleRecalculate}
            onRestart={handleRestart}
          />
        )}
      </div>

      <Toast
        message={toast.message}
        type={toast.type}
        visible={!!toast.message}
        onClose={() => setToast({ message: '', type: 'error' })}
      />
    </div>
  );
};

export default AIInterviewPage;
